import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Clock, Users } from 'lucide-react';
import api from '@/api/api';

const ManageClasses = () => {
  const [classes, setClasses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [sections, setSections] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [routineSettings, setRoutineSettings] = useState(null);
  
  const [formData, setFormData] = useState({
    id: '',
    day: 'Monday',
    course_id: '',
    semester_id: '',
    section_id: '',
    department_id: '',
    teacher_id: '',
    subject_id: '',
    start_time: '',
    end_time: '',
    is_active: true
  });

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    fetchRoutineSettings();
    fetchClasses();
    fetchCourses();
    fetchSemesters();
    fetchSections();
    fetchDepartments();
    fetchTeachers();
    fetchSubjects();
  }, []);

  const fetchRoutineSettings = async () => {
    try {
      const response = await api.get(`/routine/settings`);
      setRoutineSettings(response.data);
    } catch (error) {
      console.error('Error fetching routine settings:', error);
    }
  };

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/routine/classes`);
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if a class is outside working days
  const isOutsideWorkingDays = (day) => {
    if (!routineSettings || !routineSettings.working_days) return false;
    return !routineSettings.working_days.includes(day);
  };

  const fetchCourses = async () => {
    try {
      const response = await api.get(`/master-settings/courses`);
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSemesters = async () => {
    try {
      const response = await api.get(`/master-settings/semesters`);
      setSemesters(response.data);
    } catch (error) {
      console.error('Error fetching semesters:', error);
    }
  };

  const fetchSections = async () => {
    try {
      const response = await api.get(`/master-settings/sections`);
      setSections(response.data);
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get(`/master-settings/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await api.get(`/staff/`);
      setTeachers(response.data);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await api.get(`/master-settings/subjects`);
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  // Filter semesters based on selected course
  const getFilteredSemesters = () => {
    if (!formData.course_id) return [];
    return semesters.filter(sem => sem.course_id === formData.course_id);
  };

  // Filter teachers based on selected department
  const getFilteredTeachers = () => {
    if (!formData.department_id) return [];
    // Get department name from selected department_id
    const selectedDept = departments.find(d => d.id === formData.department_id);
    if (!selectedDept) return [];
    
    // Filter teachers by matching department name
    return teachers.filter(teacher => 
      teacher.department && teacher.department.toLowerCase() === selectedDept.name.toLowerCase()
    );
  };

  // Handle course change - reset semester
  const handleCourseChange = (courseId) => {
    setFormData({ 
      ...formData, 
      course_id: courseId,
      semester_id: ''
    });
  };

  // Handle department change - reset teacher
  const handleDepartmentChange = (deptId) => {
    setFormData({ 
      ...formData, 
      department_id: deptId,
      teacher_id: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (formData.id) {
        await api.put(`/routine/classes/${formData.id}`, formData);
      } else {
        await api.post(`/routine/classes`, formData);
      }
      alert('Class saved successfully!');
      setShowModal(false);
      resetForm();
      fetchClasses();
    } catch (error) {
      console.error('Error saving class:', error);
      alert('Failed to save class. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    try {
      await api.delete(`/routine/classes/${id}`);
      alert('Class deleted successfully!');
      fetchClasses();
    } catch (error) {
      console.error('Error deleting class:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to delete class: ${errorMsg}`);
    }
  };

  const handleEdit = (classItem) => {
    setFormData(classItem);
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      id: '',
      day: 'Monday',
      course_id: '',
      semester_id: '',
      section_id: '',
      department_id: '',
      teacher_id: '',
      subject_id: '',
      start_time: '',
      end_time: '',
      is_active: true
    });
  };

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.name : 'N/A';
  };

  const getSemesterName = (semId) => {
    const sem = semesters.find(s => s.id === semId);
    return sem ? sem.name : 'N/A';
  };

  const getSectionName = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    return section ? section.name : 'N/A';
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher ? (teacher.full_name || teacher.name || 'N/A') : 'N/A';
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.name : 'N/A';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Classes</h1>
          <p className="text-sm text-gray-500 mt-1">Create and manage class schedules</p>
          {routineSettings && routineSettings.working_days && (
            <p className="text-xs text-blue-600 mt-1">
              📅 Working Days: {routineSettings.working_days.join(', ')} ({routineSettings.working_days.length} days/week)
            </p>
          )}
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add New Class
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Classes</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{classes.length}</p>
            </div>
            <Users className="h-12 w-12 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Classes</p>
              <p className="text-3xl font-bold text-green-600 mt-1">
                {classes.filter(c => c.is_active).length}
              </p>
            </div>
            <Clock className="h-12 w-12 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Inactive Classes</p>
              <p className="text-3xl font-bold text-red-600 mt-1">
                {classes.filter(c => !c.is_active).length}
              </p>
            </div>
            <Clock className="h-12 w-12 text-red-500" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Day</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Semester</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Section</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teacher</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan="9" className="px-6 py-8 text-center text-gray-500">Loading...</td></tr>
            ) : classes.length === 0 ? (
              <tr><td colSpan="9" className="px-6 py-8 text-center text-gray-500">No classes found. Add a new class to get started.</td></tr>
            ) : (
              classes.map((classItem) => {
                const outsideWorkingDays = isOutsideWorkingDays(classItem.day);
                return (
                <tr key={classItem.id} className={`hover:bg-gray-50 ${outsideWorkingDays ? 'bg-yellow-50' : ''}`}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      {classItem.day}
                      {outsideWorkingDays && (
                        <span className="px-2 py-0.5 bg-yellow-200 text-yellow-800 text-xs rounded" title="Outside working days">
                          ⚠️
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{classItem.start_time} - {classItem.end_time}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{getCourseName(classItem.course_id)}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{getSemesterName(classItem.semester_id)}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{getSectionName(classItem.section_id)}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{getSubjectName(classItem.subject_id)}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{getTeacherName(classItem.teacher_id)}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      classItem.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {classItem.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleEdit(classItem)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(classItem.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              )})
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{formData.id ? 'Edit Class' : 'Add New Class'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Day Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Day *
                    {routineSettings && routineSettings.working_days && (
                      <span className="text-xs text-gray-500 ml-2">
                        (Working days: {routineSettings.working_days.length})
                      </span>
                    )}
                  </label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={formData.day}
                    onChange={(e) => setFormData({ ...formData, day: e.target.value })}
                  >
                    {daysOfWeek.map(day => {
                      const isWorkingDay = !routineSettings || !routineSettings.working_days || routineSettings.working_days.includes(day);
                      return (
                        <option 
                          key={day} 
                          value={day}
                          disabled={!isWorkingDay}
                        >
                          {day} {!isWorkingDay ? '(Non-working)' : ''}
                        </option>
                      );
                    })}
                  </select>
                  {formData.day && isOutsideWorkingDays(formData.day) && (
                    <p className="text-xs text-yellow-600 mt-1">
                      ⚠️ Warning: This day is outside configured working days
                    </p>
                  )}
                </div>

                {/* Course Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Course *</label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={formData.course_id}
                    onChange={(e) => handleCourseChange(e.target.value)}
                  >
                    <option value="">Select Course</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>{course.name}</option>
                    ))}
                  </select>
                </div>

                {/* Semester Dropdown (Connected to Course) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Semester *
                    {formData.course_id && getFilteredSemesters().length === 0 && (
                      <span className="text-xs text-orange-600 ml-2">(No semesters)</span>
                    )}
                  </label>
                  <select
                    required
                    disabled={!formData.course_id}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      !formData.course_id ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    value={formData.semester_id}
                    onChange={(e) => setFormData({ ...formData, semester_id: e.target.value })}
                  >
                    <option value="">{!formData.course_id ? 'Select Course First' : 'Select Semester'}</option>
                    {getFilteredSemesters().map(sem => (
                      <option key={sem.id} value={sem.id}>{sem.name}</option>
                    ))}
                  </select>
                </div>

                {/* Section Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Section *</label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={formData.section_id}
                    onChange={(e) => setFormData({ ...formData, section_id: e.target.value })}
                  >
                    <option value="">Select Section</option>
                    {sections.map(section => (
                      <option key={section.id} value={section.id}>{section.name}</option>
                    ))}
                  </select>
                </div>

                {/* Department Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={formData.department_id}
                    onChange={(e) => handleDepartmentChange(e.target.value)}
                  >
                    <option value="">Select Department</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                </div>

                {/* Teacher Dropdown (Connected to Department) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teacher *
                    {formData.department_id && getFilteredTeachers().length === 0 && (
                      <span className="text-xs text-orange-600 ml-2">(No teachers)</span>
                    )}
                  </label>
                  <select
                    required
                    disabled={!formData.department_id}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      !formData.department_id ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    value={formData.teacher_id}
                    onChange={(e) => setFormData({ ...formData, teacher_id: e.target.value })}
                  >
                    <option value="">{!formData.department_id ? 'Select Department First' : 'Select Teacher'}</option>
                    {getFilteredTeachers().map(teacher => (
                      <option key={teacher.id} value={teacher.id}>{teacher.full_name || teacher.name}</option>
                    ))}
                  </select>
                </div>

                {/* Subject Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={formData.subject_id}
                    onChange={(e) => setFormData({ ...formData, subject_id: e.target.value })}
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(subject => (
                      <option key={subject.id} value={subject.id}>{subject.name} ({subject.code})</option>
                    ))}
                  </select>
                </div>

                {/* Start Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time *</label>
                  <input
                    type="time"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  />
                </div>

                {/* End Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time *</label>
                  <input
                    type="time"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  />
                </div>
              </div>

              {/* Active Checkbox */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                  Active
                </label>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {loading ? 'Saving...' : (formData.id ? 'Update Class' : 'Create Class')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageClasses;
